import { authClient, getServiceURL } from '@/utils'
import { formatYYMMDD } from '@/utils/formatDate'
import { productConstants } from '@/constants'
import Toggles from '@domain/toggles/Toggles'
import DataProvider from '@domain/application/DataProvider'
import DataSource from '@domain/application/DataSourceAPIImplementation'
import LuminariesFactory from '@domain/factory/Luminaries'
import ProjectConverter from '@domain/converters/ProjectConverter'
import ArticleCollection from '@domain/catalog/ArticleCollection'
import Space from '@domain/composition/Space'
import ProjectApi from '@api/ProjectApi'
import ArticleCollectionApi from '@api/ArticleCollectionApi'


const getProject = ({ projectId, lang, withPrices = false, isProjectPage = false }) => {
	return authClient.get(`${getServiceURL('projects')}/v1/projects/${projectId}`, {
		params: {
			lang,
			prices: withPrices,
			parseProducts: isProjectPage
		}
	})
}

const getProjectv2 = ({ projectId, lang, withPrices = false, isProjectPage = false }) => {
	return authClient.get(`${getServiceURL('apiv2')}/project/${projectId}`, {
		params: {
			lang,
			prices: withPrices,
			parseProducts: isProjectPage
		}
	})
}

const getProjectToBudgetV2 = ({ projectId, lang, withPrices = false, isProjectPage = false }) => {
	return authClient.get(`${getServiceURL('apiv2')}/project-to-budget`, {
		params: {
			projectId,
			lang,
			prices: withPrices,
			parseProducts: isProjectPage
		}
	})
}

const createProject = ({ catalogId, products, name }) => {
	return authClient.post(`${getServiceURL('projects')}/v1/projects`, {
		name,
		catalogId,
		products
	})
}

const createProjectv2 = (body) => {
	return authClient.post(`${getServiceURL('apiv2')}/project`, body)
}

const upsertVersionV2 = (projectId) => {
	return authClient.post(`${getServiceURL('apiv2')}/project/${projectId}/version/update`)
}

const duplicateProject = (projectId) => {
	return authClient.post(`${getServiceURL('projects')}/v1/projects/${projectId}/clone`)
}

const versionProject = (projectId, email) => {
	const queryParams = email ? `?email=${email}` : '';
	return authClient.post(`${getServiceURL('apiv2')}/project/${projectId}/version${queryParams}`)
}

const getProjectVersions = (projectId) => {
	return authClient.get(`${getServiceURL('apiv2')}/project/${projectId}/versions`)
}

const createProjectWithReferences = ({ catalogId, collection, referencesDetails, projectName }) => {
	return authClient.post(`${getServiceURL('projects')}/v1/projects`, {
		name: projectName,
		catalogId,
		products: referencesDetails.map((referenceDetails) => ({
			type: referenceDetails.type || 'REGULAR',
			familyId: referenceDetails.familyId,
			model: referenceDetails.model,
			quantity: referenceDetails.quantity,
			sublevel: collection.sublevel,
			subFamily: collection.subFamily,
			applicationLocation: {
				id: collection.applicationLocation.id
			},
			...(referenceDetails.configuratorType && { configuratorType: referenceDetails.configuratorType })
		}))
	})
}

const addProduct = ({ projectId, product }) => {
	return authClient.post(
		`${getServiceURL('projects')}/v1/projects/${projectId}/products`,
		[{ quantity: 1, ...product }]
	)
}

const addProducts = ({ projectId, products }) => {
	return authClient.post(
		`${getServiceURL('projects')}/v1/projects/${projectId}/products`,
		products.map((product) => ({ quantity: 1, ...product }))
	)
}

const addProductsV2 = ({ projectId, products, config }) => {
	return authClient.post(`${getServiceURL('apiv2')}/project`, {
		projectId,
		products,
		config
	})
}

const getArticleCollection = async ({ catalog, applicationLocation, subfamily }) => {
	const response = await ArticleCollectionApi.getModels(catalog, applicationLocation, subfamily, 'without') || []
	if (response.message) {
		return
	}
	const models = response.find(elem => elem.catalogId == catalog)?.models || []
	const modelIds = models.map(model => model.model).sort((a, b) => a - b)

	const variations = await ArticleCollectionApi.getArticleCollection(modelIds, catalog)
	if (!variations) return

	return ArticleCollection.getInstance(variations)
}

const addProductByRef = async ({ projectId, reference }) => {
	const lightData = await DataProvider.getInformationByReference(new DataSource(), reference)

	const luminary = LuminariesFactory.createLuminary(lightData)

	const light = Array.isArray(luminary.getLights())
		? luminary.getLights()[0].element
		: luminary.getLights().element
	light.setAttributesByReference(reference)

	const applicationLocation = light.getApplicationLocation()[0].id
	const subfamily = light.getSubFamily()
	const catalog = reference.includes('-') ? 21 : 16


	let space = new Space([], null, null, null, null, null, null, 'ATTRIBUTES_CONFIGURATOR', 'PRIVATE_AREA')
	space = space.addElement({ element: luminary, options: { position: [0, 0, 0] } }, space.getId())
	const articleCollection = await getArticleCollection({ catalog, applicationLocation, subfamily })
	const BOM = ProjectConverter.convertSpaceToBOM(space, articleCollection)

	return await ProjectApi.addToProject(space, catalog, BOM, projectId)
}

const addProductsByRef = ({ projectId, collection, referencesDetails }) => {
	return authClient.post(
		`${getServiceURL('projects')}/v1/projects/${projectId}/products`,
		referencesDetails.map((referenceDetails) => ({
			type: referenceDetails.type || 'REGULAR',
			familyId: referenceDetails.familyId,
			model: referenceDetails.model,
			quantity: referenceDetails.quantity || 1,
			sublevel: collection.sublevel,
			subFamily: collection.subFamily,
			applicationLocation: {
				id: collection.applicationLocation.id
			},
			...(referenceDetails.configuratorType && { configuratorType: referenceDetails.configuratorType })
		}))
	)
}

const createBudget = ({ budgetDetails, formData }) => {
	const body = {
		projectId: budgetDetails.id,
		projectName: budgetDetails.name,
		budgetNumber: formData.budgetNumber,
		clientName: formData.clientName,
		createdBy: formData.createdBy,
		creationDate: formatYYMMDD(formData.creationDate),
		validationDate: formatYYMMDD(formData.validationDate),
		currency: budgetDetails.currency,
		discount: budgetDetails?.discount || 0,
		surcharge: budgetDetails.surcharge || 0,
		iva: budgetDetails.iva || 0,
		discountValue: budgetDetails?.discountValue || 0,
		surchargeValue: budgetDetails.surchargeValue || 0,
		ivaValue: budgetDetails.ivaValue || 0,
		subTotalNet: budgetDetails.subTotal || 0,
		totalNet: budgetDetails.netTotal || 0,
		comments: budgetDetails.comment || '',
		products: Object.values(budgetDetails.products.byId).reduce((acc, product) => {
			if (product.quantity <= 0) {
				return acc
			}

			if (product?.type === productConstants.PRODUCT_TYPE_CREATION) {
				return [
					...acc,
					{
						...product,
						discount: product?.discount || 0,
						pvp: product?.quantity * product?.prices.unitGross,
						netPrice: product?.discount
							? product?.quantity * product?.prices?.unitGross * (1 - product.discount / 100)
							: product?.quantity * product?.prices?.unitGross,
						discountValue: product?.discount ? product?.prices?.unitGross * (product.discount / 100) : 0,
						unitPvp: product?.prices?.unitGross,
						compositions: product.compositions.reduce((acc, composition) => {
							return [
								...acc,
								{
									...composition,
									pvp: product?.quantity * composition?.prices?.unitGross,
									netPrice: product?.discount
										? product?.quantity * composition?.prices?.unitGross * (1 - product.discount / 100)
										: product?.quantity * composition?.prices?.unitGross,
									...(composition.accessories && {
										accessories: composition.accessories.reduce((acc, accessory) => {
											return [
												...acc,
												{
													...accessory,
													netPrice: accessory?.prices?.totalNet,
													pvp: accessory?.quantity * accessory?.prices?.unitGross,
													unitPvp: accessory?.prices?.unitGross
												}
											]
										}, [])
									}),
									articles: composition.articles.reduce((acc, article) => {
										return [
											...acc,
											{
												...article,
												netPrice: article?.prices?.totalNet,
												pvp: article?.quantity * article?.prices?.unitGross,
												unitPvp: article?.prices.unitGross,
												...(!!article?.parts.length && {
													parts: article.parts.reduce((acc, part) => {
														return [
															...acc,
															{
																...part,
																netPrice: part?.prices?.totalNet,
																pvp: part?.quantity * part?.prices?.unitGross,
																unitPvp: part?.prices.unitGross
															}
														]
													}, [])
												})
											}
										]
									}, [])
								}
							]
						}, [])
					}
				]
			} else {
				return [
					...acc,
					{
						...(Toggles.isEnabled('projectRequestBudgetV2') && { articleCode: product.articleCode }),
						referenceCode: product.referenceCode || 0,
						familyId: product.familyId,
						subFamily: product.subFamily,
						applicationLocation: product.applicationLocation,
						model: product.model,
						hasAttributes: product.hasAttributes,
						attributes: product.attributes,
						quantity: product.quantity,
						lumensLed: product.lumensLed,
						watts: product.watts,
						configuratorType: product.configuratorType,
						isPersonalized: product.isPersonalized,
						type: product.type,
						hasOrderReq: product.hasOrderReq,
						discount: product.discount || 0,
						pvp: product.quantity * product?.prices.unitGross,
						netPrice: product.discount
							? product.quantity * product?.prices.unitGross * (1 - product.discount / 100)
							: product.quantity * product?.prices.unitGross,
						discountValue: product.discount ? product?.prices.unitGross * (product.discount / 100) : 0,
						unitPvp: product?.prices.unitGross
					}
				]
			}
		}, [])
	}
	let url = `${getServiceURL('projects')}/v1/budget`
	if(Toggles.isEnabled('sendOfferWithDownloadBudget')){
		url = `${getServiceURL('apiv2')}/budget`
	}
	return authClient.post(url, body)
}

const createBudgetUsa = ({ budgetDetails, formData, isFinalQuote = false }) => {
	const body = {
		projectId: budgetDetails.id,
		projectName: budgetDetails.name,
		budgetNumber: formData.budgetNumber,
		budgetAlias: formData.budgetAlias,
		specifier: {
			email: formData.specEmail,
			companyName: formData.specCompanyName
		},
		...(isFinalQuote && {
			purchaserEmail: formData.purchaserEmail,
			country: {
				geonameId: formData.country.geonameId,
				name: formData.country.name
			},
			state: {
				geonameId: formData.state.geonameId,
				name: formData.state.name
			},
			city: {
				geonameId: formData.city.geonameId,
				name: formData.city.name
			}
		}),
		createdBy: formData.createdBy,
		creationDate: formatYYMMDD(formData.creationDate),
		currency: budgetDetails.currency,
		discount: budgetDetails.discount || 0,
		surcharge: budgetDetails.surcharge || 0,
		iva: budgetDetails.iva || 0,
		discountValue: budgetDetails.discountValue || 0,
		surchargeValue: budgetDetails.surchargeValue || 0,
		ivaValue: budgetDetails.ivaValue || 0,
		subTotalNet: budgetDetails.subTotal || 0,
		totalNet: budgetDetails.netTotal || 0,
		comments: budgetDetails.comment || '',
		products: Object.values(budgetDetails.products.byId).reduce((acc, product) => {
			if (product.quantity <= 0) {
				return acc
			}

			if (product?.type === productConstants.PRODUCT_TYPE_CREATION) {
				return [
					...acc,
					{
						...product,
						discount: product?.discount || 0,
						pvp: product.quantity * product?.prices.unitGross,
						netPrice: product.discount
							? product.quantity * product?.prices?.unitGross * (1 - product.discount / 100)
							: product.quantity * product?.prices?.unitGross,
						discountValue: product.discount ? product?.prices?.unitGross * (product.discount / 100) : 0,
						unitPvp: product?.prices?.unitGross,
						compositions: product.compositions.reduce((acc, composition) => {
							return [
								...acc,
								{
									...composition,
									pvp: composition?.prices?.unitGross,
									netPrice: composition?.prices?.unitDiscount,
									...(composition.accessories && {
										accessories: composition.accessories.reduce((acc, accessory) => {
											return [
												...acc,
												{
													...accessory,
													netPrice: accessory?.prices?.totalNet,
													pvp: accessory?.quantity * accessory?.prices?.unitGross,
													unitPvp: accessory?.prices?.unitGross
												}
											]
										}, [])
									}),
									articles: composition.articles.reduce((acc, article) => {
										return [
											...acc,
											{
												...article,
												netPrice: article?.prices?.totalNet,
												pvp: article?.quantity * article?.prices?.unitGross,
												unitPvp: article?.prices?.unitGross,
												...(!!article?.parts.length && {
													parts: article.parts.reduce((acc, part) => {
														return [
															...acc,
															{
																...part,
																netPrice: part?.prices?.totalNet,
																pvp: part?.quantity * part?.prices?.unitGross,
																unitPvp: part?.prices?.unitGross
															}
														]
													}, [])
												})
											}
										]
									}, [])
								}
							]
						}, [])
					}
				]
			} else {
				return [
					...acc,
					{
						referenceCode: product.referenceCode || 0,
						familyId: product.familyId,
						subFamily: product.subFamily,
						applicationLocation: product.applicationLocation,
						model: product.model,
						hasAttributes: product.hasAttributes,
						attributes: product.attributes,
						quantity: product.quantity,
						lumensLed: product.lumensLed,
						watts: product.watts,
						configuratorType: product.configuratorType,
						isPersonalized: product.isPersonalized,
						type: product.type,
						hasOrderReq: product.hasOrderReq,
						discount: product.discount || 0,
						pvp: product.quantity * product?.prices.unitGross,
						netPrice: product.discount ?
							product.quantity * product?.prices.unitGross * (1 - product.discount / 100)
							:
							product.quantity * product?.prices.unitGross,
						discountValue: product.discount ? product?.prices.unitGross * (product.discount / 100) : 0,
						unitPvp: product?.prices.unitGross
					}
				]
			}
		}, [])
	}
	return isFinalQuote
		? authClient.post(`${getServiceURL('projects')}/v1/budget/final-quote`, body)
		: authClient.post(`${getServiceURL('projects')}/v1/budget`, body)
}

const getPreviousBudgets = ({ projectId, lang }) => {
	return authClient.get(
		`${getServiceURL('projects')}/v1/budget/project/${projectId}`,
		{
			params: {
				lang
			}
		}
	)
}

const getProjectRegistration = ({ projectId, lang }) => {
	return authClient.get(
		`${getServiceURL('support')}/v1/support-request/project-registration/${projectId}`,
		{
			params: {
				lang
			}
		}
	)
}
const getBudgetRequestInfo = ({ projectId, lang, catalogId }) => {
	return authClient.get(
		`${getServiceURL('support')}/v1/support-request/info-project-budget/${projectId}`,
		{
			params: {
				lang,
				catalogId
			}
		}
	)
}

const cloneProduct = async ({ projectId, productId }) => {
	return authClient.post(
		`${getServiceURL('apiv2')}/projects/${projectId}/products/${productId}/clone`
	)
}

const deleteProduct = ({ projectId, productId }) => {
	return authClient.delete(
		`${getServiceURL('projects')}/v1/projects/${projectId}/products/${productId}`
	)
}

const updateProduct = ({ projectId, productId, attributesToUpdate }) => {
	return authClient.put(
		`${getServiceURL('projects')}/v1/projects/${projectId}/products/${productId}`,
		attributesToUpdate
	)
}

const updateProject = ({ projectId, fieldsToUpdate }) => {
	return authClient.put(
		`${getServiceURL('projects')}/v1/projects/${projectId}`,
		fieldsToUpdate
	)
}

const updateProjectv2 = (body) => {
	return authClient.post(`${getServiceURL('apiv2')}/project`, body)
}

const deleteProject = (projectId) => {
	return authClient.delete(`${getServiceURL('projects')}/v1/projects/${projectId}`)
}

const getTooltipInfo = ({ projectId }) => {
	if (Toggles.isEnabled("getSupportTooltipInfoV2")) {
		return authClient.get(
			`${getServiceURL('apiv2')}/support/tooltip-info/${projectId}`
		)
	} else {
		return authClient.get(
			`${getServiceURL('projects')}/v1/projects/tooltip-info/${projectId}`
		)
	}
}

const getProjectPersonalizedProducts = ({ projectId, lang }) => {
	return authClient.get(
		`${getServiceURL('projects')}/v1/projects/${projectId}/products`,
		{
			params: {
				isPersonalized: 1,
				lang
			}
		}
	)
}

const createRequestBudget = ({ lang, files, siteLocation, catalogId, description, project }) => {
	let url = Toggles.isEnabled('projectRequestBudgetV2') ? `${getServiceURL('apiv2')}/support-request/project-budget` : `${getServiceURL('support')}/v1/support-request/project-budget`

	return authClient.post(url, {
		lang,
		files,
		siteLocation,
		catalogId,
		description,
		project,
		_supportType: 'project-budget'
	})
}

const createProjectRegistration = ({ registrationData }) => {
	return authClient.post(
		`${getServiceURL('support')}/v1/support-request/project-registration-usa`,
		registrationData
	)
}

const assignProjectToThirdParty = ({ projectId, email }) => {
	const url = Toggles.isEnabled('projectsUploadCrmWhenAssignedToThirdParties') ? `${getServiceURL('apiv2')}/${projectId}/assign-thirdparty` : `${getServiceURL('projects')}/v1/projects/${projectId}/assign-thirdparty`
	return authClient.post(
		url,
		{
			email
		}
	)
}

const getProjectTechInfo = (projectId) => {
	return authClient.get(
		`${getServiceURL('projects')}/v1/projects/${projectId}/tech-info-actions`
	)
}

const downloadProjectFiles = (docs, lang, catalogId) => {
	return authClient.post(
		`${getServiceURL('documents')}/v1/docs-web`,
		{
			docs
		},
		{
			params: {
				lang,
				catalogId
			}
		}
	)
}

const validateProduct = ({ projectId, productId }) => {
	return authClient.put(`${getServiceURL('apiv2')}/project/${projectId}/product/${productId}/validate`)
}

const getPDFnewFlow = ({ creationCode, lang }) => {
	return authClient.get(`${getServiceURL('apiv2')}/documents/generate-creation-specsheet/${creationCode}?lang=${lang}`)
}

const updateProjectName = (projectId, projectName ) => {
	return authClient.put(`${getServiceURL('apiv2')}/project/${projectId}/projectName`, {
		projectName: projectName
	})
}
const getProjectName = (projectId ) => {
	return authClient.get(`${getServiceURL('apiv2')}/project/${projectId}/projectName`)
}



export default {
	addProduct,
	addProductByRef,
	addProducts,
	addProductsV2,
	addProductsByRef,
	assignProjectToThirdParty,
	cloneProduct,
	createBudget,
	createBudgetUsa,
	createProject,
	createProjectv2,
	upsertVersionV2,
	createProjectWithReferences,
	createProjectRegistration,
	createRequestBudget,
	deleteProduct,
	deleteProject,
	duplicateProject,
	versionProject,
	getProjectVersions,
	getPreviousBudgets,
	getProject,
	getProjectv2,
	getProjectToBudgetV2,
	getProjectPersonalizedProducts,
	getProjectRegistration,
	getBudgetRequestInfo,
	getProjectTechInfo,
	getTooltipInfo,
	updateProduct,
	updateProject,
	updateProjectv2,
	downloadProjectFiles,
	validateProduct,
	getPDFnewFlow,
	updateProjectName,
	getProjectName
}
